import React, { useState } from 'react';
import './style/CagnotteBox.css';
import { useStripeCagnotteContext } from '../stripe/StripeContextCagnotte';
import { useUserContext } from '../contexts/UserContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const baseUrl = process.env.REACT_APP_API_URL;
const endpointAddCagnotte = `${baseUrl}/add-cagnotte-link`;

function CagnotteBox({ event }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(event?.cagnotte_url || '');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loadingSave, setLoadingSave] = useState(false);
  const { createCagnotteSession, loading } = useStripeCagnotteContext();
  const { tokens } = useUserContext();

  const isValidCagnotteURL = (url) => {
    const allowedDomains = [
      'leetchi.com', 'lydia-app.com', 'mon.lydia-app.com', 'paypal.me',
      'kisskissbankbank.com', 'helloasso.com', 'lepotcommun.fr', 'cagnotte.me',
      'gofundme.com', 'papayoux.com', 'tipeee.com', 'ulule.com'
    ];
    try {
      const parsedUrl = new URL(url);
      return allowedDomains.some(domain => parsedUrl.hostname.includes(domain));
    } catch (e) {
      return false;
    }
  };

  const handleSave = async () => {
    setLoadingSave(true);
    setMessage({ text: '', type: '' });

    if (!isValidCagnotteURL(url)) {
      setMessage({ text: t('cagnotte.invalidUrl'), type: 'error' });
      setLoadingSave(false);
      return;
    }

    try {
      const response = await axios.post(
        endpointAddCagnotte,
        JSON.stringify({ eventId: event.eventId, cagnotte_url: url }),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens?.accessToken}`,
          },
        }
      );

      let parsed = {};
      const body = response?.data?.body;
      if (body) {
        try {
          parsed = JSON.parse(body);
        } catch (e) {
          console.error("[CagnotteBox] JSON parse error:", e);
        }
      } else if (typeof response.data === "object") {
        parsed = response.data;
      }

      if (parsed.statusCode >= 400) throw new Error(parsed.message || "Unknown error");
      setMessage({ text: parsed.message || t('cagnotte.success'), type: 'success' });
    } catch (err) {
      console.error("[CagnotteBox] Save error:", err);
      let msg = t('cagnotte.connectionError');
      if (err.response && err.response.data) {
        try {
          let res = typeof err.response.data === "string"
            ? JSON.parse(err.response.data)
            : err.response.data;
          if (res.body) res = JSON.parse(res.body);
          if (res.message) msg = res.message;
        } catch {}
      }
      setMessage({ text: msg, type: 'error' });
    } finally {
      setLoadingSave(false);
    }
  };

  const handleActivateCagnotte = async () => {
    try {
      const priceId = "price_1SMAPmLuXqiGvqVbMs1ctaoT";
      const sessionUrl = await createCagnotteSession(priceId, event.eventId);
      window.location.href = sessionUrl;
    } catch (err) {
      console.error("Activation error:", err);
    }
  };

  if (!event) return null;
  const isEnabled = event.cagnotte_enable === "true";

  return (
    <div className="cagnotte-box">
      <h4 className="cagnotte-title">
        {t('cagnotte.title')}
        <div className="tooltip-container">
          <div className="info-icon">i</div>
          <div className="tooltip-text">
            <strong>{t('cagnotte.allowedTitle')}</strong>
            <ul>
              {[
                'leetchi.com', 'lydia-app.com', 'mon.lydia-app.com', 'paypal.me',
                'kisskissbankbank.com', 'helloasso.com', 'lepotcommun.fr',
                'cagnotte.me', 'gofundme.com', 'papayoux.com', 'tipeee.com', 'ulule.com'
              ].map((domain, idx) => (
                <li key={idx}>🔗 {domain}</li>
              ))}
            </ul>
            {t('cagnotte.tooltip')}
          </div>
        </div>
      </h4>

      {isEnabled ? (
        <>
          <label htmlFor="cagnotte-url">{t('cagnotte.linkLabel')}</label>
          <input
            id="cagnotte-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="cagnotte-input"
          />
          <button onClick={handleSave} className="cagnotte-btn" disabled={loadingSave}>
            {loadingSave ? t('cagnotte.saving') : t('cagnotte.validate')}
          </button>

          {event.cagnotte_url && (
            <div className="cagnotte-link">
              {t('cagnotte.currentLink')} <a href={event.cagnotte_url} target="_blank" rel="noreferrer">{event.cagnotte_url}</a>
            </div>
          )}

          {message.text && (
            <p className={`cagnotte-message ${message.type === 'error' ? 'error' : 'success'}`}>
              {message.text}
            </p>
          )}
        </>
      ) : (
        <div className="cagnotte-disabled">
          <p>{t('cagnotte.activate1')}</p>
          <p>{t('cagnotte.activate2')}</p>
          <button onClick={handleActivateCagnotte} disabled={loading} className="cagnotte-btn">
            {loading ? t('cagnotte.redirecting') : t('cagnotte.activateButton')}
          </button>
        </div>
      )}
    </div>
  );
}

export default CagnotteBox;
